import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Plasticos Polcen - Bolsas de Polietileno de Alta Calidad',
  description = 'Fabricamos bolsas de polietileno de baja densidad (LDPE) de alta calidad. Especialistas en bolsas comerciales, industriales y domesticas. Entrega rapida en San Juan, Argentina.',
  keywords = 'bolsas plasticas, polietileno, LDPE, bolsas comerciales, bolsas industriales, embalaje, San Juan, Argentina, plasticos',
  image = '/images/empresa.jpg',
  url,
  type = 'website',
  siteName = 'Plasticos Polcen'
}) => {
  const location = useLocation();
  const siteUrl = (process.env.REACT_APP_SITE_URL || 'https://polcenplast.appteck.com.ar').replace(/\/+$/, '');
  const fullTitle = title.includes('Plasticos Polcen') ? title : `${title} | Plasticos Polcen`;
  const routeUrl = `${siteUrl}${location.pathname}`;
  const fullUrl = url ? (url.startsWith('http') ? url : `${siteUrl}${url}`) : routeUrl;
  const fullImage = image.startsWith('http') ? image : `${siteUrl}${image}`;

  useEffect(() => {
    document.title = fullTitle;

    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${property}"]`) as HTMLMetaElement | null;

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;

      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    const upsertJsonLd = (id: string, data: Record<string, unknown>) => {
      let script = document.querySelector(`script#${id}`) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };

    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', 'Plasticos Polcen');
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('language', 'es');
    updateMetaTag('revisit-after', '7 days');

    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', fullImage, true);
    updateMetaTag('og:site_name', siteName, true);
    updateMetaTag('og:locale', 'es_AR', true);

    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:url', fullUrl);
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', fullImage);

    updateLinkTag('canonical', fullUrl);

    upsertJsonLd('org-schema', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Plasticos Polcen',
      url: siteUrl,
      logo: `${siteUrl}/images/logo.jpg`,
      description: 'Fabricamos bolsas de polietileno de baja densidad (LDPE) de alta calidad en San Juan, Argentina.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Santiago de Liniers 1239',
        addressLocality: 'Rivadavia',
        addressRegion: 'San Juan',
        addressCountry: 'AR'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+54-264-411-4422',
        contactType: 'customer service',
        email: 'plasticospolcen@gmail.com',
        availableLanguage: 'Spanish'
      }
    });

    upsertJsonLd('local-business-schema', {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Plasticos Polcen',
      image: `${siteUrl}/images/empresa.jpg`,
      description: 'Fabricamos bolsas de polietileno de baja densidad (LDPE) de alta calidad. Especialistas en bolsas comerciales, industriales y domesticas.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Santiago de Liniers 1239',
        addressLocality: 'Rivadavia',
        addressRegion: 'San Juan',
        addressCountry: 'AR'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '-31.51526048777523',
        longitude: '-68.57175370379987'
      },
      url: siteUrl,
      telephone: '+54-264-411-4422',
      email: 'plasticospolcen@gmail.com',
      openingHours: 'Mo-Fr 08:00-17:00',
      priceRange: '$$'
    });
  }, [fullTitle, description, keywords, fullImage, fullUrl, type, siteName, siteUrl]);

  return null;
};

export default SEO;
